import PageMeta from "@/components/PageMeta";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Building2,
  Check,
  Flag,
  Printer,
  ShieldCheck,
  Tag,
  Wind,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";
import {
  calculateBannerPrice,
  fetchBannerConfig,
  formatGBP,
  getFallbackConfig,
} from "@/lib/bannerPricing";
import { BannerConfigurator } from "@/components/banners/BannerConfigurator";

/* ── Static display data ── */

const EXAMPLE_CARDS = [
  {
    id: "retail-promo", title: "Shop Front & Roadside PVC",
    line: "Promote your business during race week, event seasons, and peak trading periods. Fast local print.",
    materialId: "pvc", icon: Flag,
    image: "https://customer-assets.emergentagent.com/job_00dae4b3-6d98-4555-9020-2420be566521/artifacts/y97gq6oj_Firefly_Flux_%20Retail%20Banner_retail%20storefront%20banner%2C%20modern%20shop%20exterior%2C%20sale%20promotion%20banner%20976052.jpg",
    priceConfig: { widthM: 2, heightM: 0.5, materialId: "pvc-440", eyeletId: "corner", reinforcementId: "none" },
    configCaption: "2m \u00d7 0.5m PVC, corner eyelets",
  },
  {
    id: "fence-mesh", title: "Fence & Outdoor Event Mesh",
    line: "Sponsor boards, course-side signage, and outdoor event graphics built for exposed positions.",
    materialId: "mesh", icon: Wind,
    image: "https://customer-assets.emergentagent.com/job_4e92b371-e3ab-41e3-99b0-621f89159b1e/artifacts/bre06d0z_Firefly_Gemini%20Flash_Mesh%20Banner%20%28Windy%20Outdoor%29_mesh%20banner%20on%20fence%2C%20slightly%20transparent%20material%2C%20wind%20267932.png",
    priceConfig: { widthM: 3, heightM: 1, materialId: "mesh", eyeletId: "heavy", reinforcementId: "webbing" },
    configCaption: "3m \u00d7 1m mesh, heavy eyelets, webbing",
  },
  {
    id: "site-branding", title: "Large-Format Site & Scaffold",
    line: "High-impact branding for construction sites, developments, and large-scale contractor visibility.",
    materialId: "scaffolding", icon: Building2,
    image: "https://customer-assets.emergentagent.com/job_4e92b371-e3ab-41e3-99b0-621f89159b1e/artifacts/x6nqrx1d_Firefly_Flux_PVC%20Banner_banner%2C%20excavator%2C%20dirt%20movement%2C%20industrial%20environment%2C%20AMDECOR%20CONSTRUC%20976052.jpg",
    priceConfig: { widthM: 4, heightM: 1.5, materialId: "mesh", eyeletId: "heavy", reinforcementId: "webbing" },
    configCaption: "4m \u00d7 1.5m mesh, heavy eyelets, webbing",
  },
];

const PRICE_GUIDE_EXAMPLES = [
  { label: "PVC Banner", desc: "1m \u00d7 0.5m, PVC 440gsm, no eyelets", widthM: 1, heightM: 0.5, materialId: "pvc-440", eyeletId: "none", reinforcementId: "none" },
  { label: "Retail Promo", desc: "2m \u00d7 0.5m, PVC 440gsm, corner eyelets", widthM: 2, heightM: 0.5, materialId: "pvc-440", eyeletId: "corner", reinforcementId: "none" },
  { label: "Event Banner", desc: "3m \u00d7 1m, PVC 440gsm, standard eyelets", widthM: 3, heightM: 1, materialId: "pvc-440", eyeletId: "standard", reinforcementId: "none" },
  { label: "Mesh Banner", desc: "3m \u00d7 1m, mesh, heavy eyelets, webbing", widthM: 3, heightM: 1, materialId: "mesh", eyeletId: "heavy", reinforcementId: "webbing" },
  { label: "Roll-Up / Indoor", desc: "2m \u00d7 1m, fabric, pole pockets", widthM: 2, heightM: 1, materialId: "fabric", eyeletId: "none", reinforcementId: "pole-pocket" },
  { label: "Outdoor Site", desc: "4m \u00d7 1m, PVC 550gsm, heavy eyelets, double hem", widthM: 4, heightM: 1, materialId: "pvc-550", eyeletId: "heavy", reinforcementId: "double-hem" },
  { label: "Scaffold Banner", desc: "4m \u00d7 1.5m, mesh, heavy eyelets, webbing", widthM: 4, heightM: 1.5, materialId: "mesh", eyeletId: "heavy", reinforcementId: "webbing" },
];

const MATERIAL_COMPARE_SIZE = { widthM: 3, heightM: 1 };
const MATERIAL_COMPARE = [
  { id: "pvc-440", label: "PVC 440gsm", tagline: "Best value, all-round use", eyeletId: "standard", reinforcementId: "none" },
  { id: "pvc-550", label: "PVC 550gsm", tagline: "Thicker, heavy-duty outdoor", eyeletId: "standard", reinforcementId: "none" },
  { id: "mesh", label: "Mesh", tagline: "Wind-resistant for exposed sites", eyeletId: "heavy", reinforcementId: "webbing" },
  { id: "fabric", label: "Fabric", tagline: "Premium indoor & roll-up displays", eyeletId: "none", reinforcementId: "pole-pocket" },
];

const GENERIC_QUOTE_HREF = "/request-quote?type=Business%20Signage&description=Banner%20enquiry.";

/* ── Main Page ── */

export default function BannersPage() {
  const location = useLocation();

  /* Pricing config from API */
  const [pricingConfig, setPricingConfig] = useState(getFallbackConfig());
  useEffect(() => { fetchBannerConfig().then(setPricingConfig); }, []);

  const calc = useCallback((opts) => calculateBannerPrice(opts, pricingConfig), [pricingConfig]);

  /* Hero starting price */
  const heroStartingPrice = useMemo(() => calc({ widthM: 1, heightM: 0.5, materialId: "pvc-440", eyeletId: "none", reinforcementId: "none" }).unitPrice, [calc]);

  /* Example card prices */
  const examplePrices = useMemo(() => EXAMPLE_CARDS.map((c) => calc(c.priceConfig).unitPrice), [calc]);

  /* Material comparison prices */
  const materialPrices = useMemo(() => MATERIAL_COMPARE.map((m) => {
    const { unitPrice } = calc({ ...MATERIAL_COMPARE_SIZE, materialId: m.id, eyeletId: m.eyeletId, reinforcementId: m.reinforcementId });
    return unitPrice;
  }), [calc]);

  /* Price guide */
  const guidePrices = useMemo(() => PRICE_GUIDE_EXAMPLES.map((ex) => calc({ widthM: ex.widthM, heightM: ex.heightM, materialId: ex.materialId, eyeletId: ex.eyeletId, reinforcementId: ex.reinforcementId }).unitPrice), [calc]);

  return (
    <div className="py-16 md:py-24 pb-28 md:pb-24" data-testid="banners-page">
      <PageMeta
        title="Banners | Race Week & Event Printing"
        description="PVC banners, mesh banners, sponsor boards, and outdoor event graphics. Fast turnaround for race week, shop fronts, roadside promotion, and large-format signage. Request a quote today."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Event-season strip ── */}
        <SectionFadeIn>
          <div className="mb-6 flex items-center gap-3 rounded-sm border border-red-500/20 bg-red-500/[0.06] px-5 py-3" data-testid="banners-event-strip">
            <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse" />
            <p className="text-[12px] font-medium tracking-wide text-red-300/90">
              Event season is here — fast turnaround on PVC banners, sponsor boards, and outdoor signage for the North Coast and beyond.
            </p>
          </div>
        </SectionFadeIn>

        {/* ── Hero ── */}
        <SectionFadeIn>
          <section className="rounded-sm border border-brand-border bg-[radial-gradient(circle_at_top,_rgba(203,160,82,0.12),_transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.03),rgba(10,13,20,0.96))] px-6 py-14 md:px-10 md:py-16" data-testid="banners-hero">
            <div className="grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] gap-10 items-start">
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-brand-gold">Banners, Signs & Event Graphics</p>
                <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-brand-text max-w-3xl" data-testid="banners-heading">Get your business race-week ready</h1>
                <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-brand-text-secondary" data-testid="banners-hero-copy">
                  PVC banners, mesh banners, sponsor boards, roadside signage, and outdoor event graphics — designed, printed, and finished in-house with fast local turnaround.
                </p>
                <div className="mt-5 inline-flex items-center gap-3 rounded-sm border border-brand-gold/25 bg-brand-gold/[0.06] px-5 py-3" data-testid="banners-hero-price">
                  <Tag size={16} className="text-brand-gold shrink-0" />
                  <span className="text-[14px] font-semibold text-brand-text">PVC banners from <span className="text-brand-gold">{formatGBP(heroStartingPrice)}</span></span>
                  <span className="text-[11px] text-brand-text-secondary/50">inc. print</span>
                </div>
                <div className="mt-5 flex flex-wrap gap-2" data-testid="banners-service-tags">
                  {["PVC Banners", "Mesh Banners", "Sponsor Boards", "Outdoor Signage", "Event Graphics", "Shop Fronts"].map((tag) => (
                    <span key={tag} className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[10px] uppercase tracking-[0.14em] text-brand-text-secondary/70">{tag}</span>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-3" data-testid="banners-hero-ctas">
                  <Link to={GENERIC_QUOTE_HREF} state={getRouteState(location)} className="inline-flex items-center gap-2 rounded-sm bg-brand-gold px-7 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover" data-testid="banners-hero-quote-cta">
                    Request a Banner Quote <ArrowRight size={15} />
                  </Link>
                  <button type="button" onClick={() => document.getElementById("banner-configurator-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })} className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-text transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold" data-testid="banners-hero-configure-cta">
                    Configure Your Banner <ArrowRight size={15} />
                  </button>
                </div>
              </div>
              <div className="rounded-sm border border-white/[0.06] bg-brand-bg/70 p-5" data-testid="banners-trust-section">
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">Why AM Decor</p>
                <div className="mt-4 space-y-3">
                  {[
                    { icon: Printer, title: "Fast local turnaround", copy: "Designed, printed, and finished in-house. Same-week turnaround on most banner orders." },
                    { icon: ShieldCheck, title: "Outdoor-durable print", copy: "Roland TrueVIS VG-640 output with UV-resistant inks. Built to hold up outdoors, roadside, and at events." },
                    { icon: Check, title: "Custom sizes, any format", copy: "No fixed templates. Any size, any shape — from 1m shop banners to 12m scaffold wraps." },
                  ].map((item) => (
                    <div key={item.title} className="rounded-sm border border-white/[0.05] bg-brand-surface/40 px-4 py-4">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-brand-gold"><item.icon size={18} /></span>
                        <div>
                          <p className="text-[13px] font-medium text-brand-text">{item.title}</p>
                          <p className="mt-1 text-[12px] leading-relaxed text-brand-text-secondary/65">{item.copy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Example cards ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="banner-examples-section">
            <div className="max-w-2xl">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Popular for event season</p>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">Banners built for race week and beyond</h2>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {EXAMPLE_CARDS.map((item, index) => (
                <SectionFadeIn key={item.id} delay={index * 70}>
                  <button type="button" onClick={() => document.getElementById("banner-configurator-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })} className="group h-full w-full overflow-hidden rounded-sm border border-brand-border bg-brand-surface/40 text-left transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-brand-gold/40" data-testid={`banner-example-${item.id}`}>
                    <div className="relative aspect-[16/9] overflow-hidden bg-brand-bg">
                      <img src={item.image} alt={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/80 via-[#0A0D14]/15 to-transparent" />
                      <div className="absolute bottom-3 right-3 rounded-sm bg-brand-bg/85 backdrop-blur-sm border border-brand-gold/30 px-3 py-1.5" data-testid={`banner-example-price-${item.id}`}>
                        <span className="text-[15px] font-bold text-brand-gold">{formatGBP(examplePrices[index])}</span>
                      </div>
                    </div>
                    <div className="px-5 py-5">
                      <div className="flex items-center gap-2 text-brand-gold"><item.icon size={18} /><span className="text-[10px] uppercase tracking-[0.18em]">Banner type</span></div>
                      <h3 className="mt-4 text-[18px] font-medium text-brand-text group-hover:text-brand-gold transition-colors duration-300">{item.title}</h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-brand-text-secondary">{item.line}</p>
                      <p className="mt-2 text-[11px] text-brand-text-secondary/50">{item.configCaption}</p>
                      <p className="mt-3 text-[11px] uppercase tracking-[0.16em] text-brand-gold/80">Configure this <ArrowRight size={12} className="inline" /></p>
                    </div>
                  </button>
                </SectionFadeIn>
              ))}
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Material Comparison ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="banner-material-comparison">
            <div className="max-w-2xl mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Compare materials</p>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Material pricing at a glance</h2>
              <p className="mt-2 text-[13px] text-brand-text-secondary/60">Example prices shown for a 3m &times; 1m banner with typical finishing for each material.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {MATERIAL_COMPARE.map((mat, i) => (
                <div key={mat.id} className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-5 flex flex-col" data-testid={`material-compare-${mat.id}`}>
                  <p className="text-[14px] font-semibold text-brand-text">{mat.label}</p>
                  <p className="mt-1 text-[12px] text-brand-text-secondary/55">{mat.tagline}</p>
                  <div className="mt-auto pt-4">
                    <span className="text-[22px] font-bold text-brand-gold">{formatGBP(materialPrices[i])}</span>
                    <span className="ml-1.5 text-[11px] text-brand-text-secondary/45">for 3m &times; 1m</span>
                    <p className="mt-1 text-[11px] text-brand-text-secondary/40" data-testid={`material-compare-sqm-${mat.id}`}>
                      {formatGBP(Math.round((materialPrices[i] / MATERIAL_COMPARE_SIZE.widthM / MATERIAL_COMPARE_SIZE.heightM) * 100) / 100)}/m&sup2;
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Quick Price Guide ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="banner-price-guide">
            <div className="max-w-2xl mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Quick price guide</p>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Representative banner prices</h2>
              <p className="mt-2 text-[13px] text-brand-text-secondary/60">Single-sided, quantity 1. Bulk discounts apply at 5+, 10+, and 25+ units.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="price-guide-table">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="py-3 pr-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Banner</th>
                    <th className="py-3 pr-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60 hidden sm:table-cell">Spec</th>
                    <th className="py-3 text-right text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_GUIDE_EXAMPLES.map((ex, i) => (
                    <tr key={ex.label} className="border-b border-white/[0.04] last:border-0">
                      <td className="py-3.5 pr-4">
                        <span className="text-[13px] font-medium text-brand-text">{ex.label}</span>
                        <span className="block sm:hidden mt-0.5 text-[11px] text-brand-text-secondary/50">{ex.desc}</span>
                      </td>
                      <td className="py-3.5 pr-4 text-[12px] text-brand-text-secondary/55 hidden sm:table-cell">{ex.desc}</td>
                      <td className="py-3.5 text-right">
                        <span className="text-[15px] font-bold text-brand-gold" data-testid={`guide-price-${i}`}>{formatGBP(guidePrices[i])}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[11px] text-brand-text-secondary/40">Prices are indicative and include print. Finishing, lamination, and design are quoted separately where applicable.</p>
          </section>
        </SectionFadeIn>

        {/* ── Configurator ── */}
        <div id="banner-configurator-anchor">
          <SectionFadeIn>
            <BannerConfigurator pricingConfig={pricingConfig} />
          </SectionFadeIn>
        </div>

        {/* ── Final CTA ── */}
        <SectionFadeIn>
          <section className="mt-16 rounded-sm border border-white/[0.05] bg-brand-surface/40 px-6 py-12 md:px-8" data-testid="banners-final-cta-section">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Race week. Event season. Your business front.</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-brand-text-secondary max-w-2xl">
                  Tell us the size, the location, and the deadline. We'll get your banners designed, printed, and finished fast — no templates, no minimum orders.
                </p>
              </div>
              <Link to={GENERIC_QUOTE_HREF} state={getRouteState(location)} className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-8 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover shrink-0" data-testid="banners-final-cta-button">
                Get Your Quote Now <ArrowRight size={15} />
              </Link>
            </div>
          </section>
        </SectionFadeIn>
      </div>
    </div>
  );
}
